import * as React from 'react'
import clsx from 'clsx'
import { NativeSelectInput, NativeSelectInputProps } from './NativeSelectInput'
import { formControlState, useFormControl } from '../FormControl'
import CaretDown from '../internal/svg-icons/CaretDown'
import { StandardInput, StandardInputProps } from '../StandardInput'
import { FormControlProps } from '../FormControl'
import { SxProps } from '../sx'
import { NativeSelectClasses } from './classes'
import style from './index.module.scss'

export interface NativeSelectProp
  extends Omit<StandardInputProps, 'inputProps' | 'onChange'> {
  /**
   * The option elements to populate the select with.
   * Can be some `<option>` elements.
   */
  children?: React.ReactNode
  /**
   * Override or extend the styles applied to the component.
   * @default {}
   */
  classes?: Partial<NativeSelectClasses>
  /**
   * The icon that displays the arrow.
   * @default CaretDownIcon
   */
  IconComponent?: React.ElementType
  /**
   * An `Input` element; does not have to be a material-ui specific `Input`.
   * @default <Input />
   */
  input?: React.ReactElement<any, any>
  /**
   * [Attributes](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/select#attributes) applied to the `select` element.
   */
  inputProps?: Partial<NativeSelectInputProps>
  /**
   * Callback fired when a menu item is selected.
   *
   * @param {React.ChangeEvent<HTMLSelectElement>} event The event source of the callback.
   * You can pull out the new value by accessing `event.target.value` (string).
   */
  onChange?: NativeSelectInputProps['onChange']
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps
  /**
   * The `input` value. The DOM API casts this to a string.
   */
  value?: unknown
  /**
   * The variant to use.
   */
  variant?: FormControlProps['variant']
}

const defaultInput = <StandardInput />

/**
 * An alternative to `<Select native />` with a much smaller bundle size footprint.
 */
export const NativeSelect = React.forwardRef<
  HTMLSelectElement,
  NativeSelectProp
>(function NativeSelect(props, ref) {
  const {
    className,
    children,
    classes = {},
    IconComponent = CaretDown,
    input = defaultInput,
    inputProps,
    ...other
  } = props

  const factorFormControl = useFormControl()
  const fcs = formControlState({
    props,
    factorFormControl,
    states: ['variant', 'focused', 'size'],
  })

  const { root: rootClasses, ...otherClasses } = classes
  return (
    <React.Fragment>
      {React.cloneElement(input, {
        // Most of the logic is implemented in `NativeSelectInput`.
        // The `Select` component is a simple API wrapper to expose something better to play with.
        inputComponent: NativeSelectInput,
        classes: {
          ...input.props.classes,
          root: clsx(
            input.props?.classes?.root,
            style.selectInputRoot,
            style[`size-${fcs.size}`]
          ),
          readOnly: style.readOnly,
        },
        inputProps: {
          children,
          classes: otherClasses,
          IconComponent,
          variant: fcs.variant,
          selcetSize: fcs.size,
          focused: fcs.focused,
          type: undefined, // We render a select. We can ignore the type provided by the `Input`.
          ...inputProps,
          ...(input ? input?.props?.inputProps : {}),
        },
        ref,
        ...other,
        className: clsx(rootClasses, input?.props?.className, className),
      })}
    </React.Fragment>
  )
})
